<script setup lang="ts">
import { faSlidersUp, faXmark } from '@fortawesome/pro-light-svg-icons'
import { HIDE_AT, type ActiveFilterChip, type FilterBreakpoint } from '~/utils/filter/chips'

const { breakpoint, count, searchId, chips } = defineProps<{
  breakpoint: FilterBreakpoint
  count: string
  searchId: string
  chips: ActiveFilterChip[]
  searchLabel: string
  searchPlaceholder: string
}>()

const search = defineModel<string>('search', { required: true })
const openFilter = defineModel<boolean>('openFilter', { default: false })

// Live region would announce the count on every keystroke, so only the announcements waits
const announcedCount = useDebounce(toRef(() => count), 500)

const chipRow = useTemplateRef<HTMLElement>('chipRow')

// Safe because every surface derives its chips synchronously from local refs 
async function removeChip(index: number) {
  chips?.[index]?.remove()
  await nextTick()

  const buttons = chipRow.value?.querySelectorAll<HTMLButtonElement>('button')
  if (buttons?.length) {
    buttons[Math.min(index, buttons.length - 1)]?.focus()
    return
  }

  document.getElementById(searchId)?.focus()
}
</script>

<template>
  <div class="flex flex-col gap-2xs w-full">
    <div class="flex items-center gap-s w-full" :class="HIDE_AT[breakpoint]">
      <FormSearchInput
        :id="searchId"
        v-model="search"
        resettable
        shape="square"
        size="medium"
        :label="searchLabel"
        :placeholder="searchPlaceholder"
        class="flex-1"
      />
      <Chip
        size="large"
        variant="outlined"
        :icon-left="!openFilter ? faSlidersUp : faXmark"
        class="shrink-0"
        aria-label="Filter"
        :aria-expanded="openFilter"
        :class="{ 'bg-blurple-500 text-white': openFilter }"
        @click="openFilter = true"
      >
        Filter
      </Chip>
    </div>

    <div
      v-if="chips?.length"
      ref="chipRow"
      data-testid="chip-row"
      class="flex flex-wrap items-center justify-start gap-2xs w-full min-h-5 animate-fade"
      :class="HIDE_AT[breakpoint]"
    >
      <Chip
        v-for="(chip, index) in chips"
        :key="chip.key"
        size="small"
        variant="filled"
        color="carbon"
        :icon-right="faXmark"
        :aria-label="`Fjern filter: ${chip.label}`"
        @click="removeChip(index)"
      >
        {{ chip.label }}
      </Chip>
    </div>

    <div class="flex w-full items-center justify-between gap-s">
      <p aria-hidden="true" data-testid="result-count" class="font-bold shrink-0 text-small">
        {{ count }}
      </p>
      <p class="sr-only" role="status">
        {{ announcedCount }}
      </p>
      <div v-if="$slots.actions" data-testid="actions" class="flex items-center gap-s">
        <slot name="actions" />
      </div>
    </div>

    <slot />
  </div>
</template>